/**
 * Archive browser (§21).
 *
 * A folder tree with sizes, and nothing that happens on its own. Opening the
 * archive decompressed nothing; expanding a folder decompresses nothing;
 * selecting an entry decompresses nothing. One entry is decompressed when the
 * person clicks Preview or Extract on it, and the row says how large it claims
 * to be before they do.
 *
 * That last part is the point of showing declared size and ratio in the list:
 * a 32 MB entry inside a 33 KB file is visible as such *before* anyone asks
 * for it, rather than being discovered by the machine running out of memory.
 *
 * Executables are shown, labelled, and given no way to be opened — §21 is
 * explicit that extracted content is never executed, and the surest way to
 * honour that is for the affordance not to exist.
 */
import { useMemo, useState } from 'react';
import { buildTree, extractEntry } from '@moduviewer/handlers';
import type { ArchiveEntry, ArchiveModel, ArchiveNode, HandlerContext } from '@moduviewer/handlers';
import { FORMATS_BY_ID, formatFileSize } from '@moduviewer/core';
import { Icon, iconForFormat } from '@moduviewer/design/icon';
import type { IconName } from '@moduviewer/design/icon';

export interface ArchiveViewerProps {
  readonly model: ArchiveModel;
  readonly bytes: Uint8Array;
  readonly ctx: HandlerContext;
  readonly editing: boolean;
  readonly search: string;
  readonly onRemove: (name: string) => void;
  readonly onRestore: (name: string) => void;
  /** Open an entry as a document in its own right, from already-extracted bytes. */
  readonly onPreview: (name: string, bytes: Uint8Array) => void;
  /** Save one entry to disk as an ordinary file. */
  readonly onExtract: (name: string, bytes: Uint8Array) => void;
}

export function ArchiveViewer({
  model, bytes, ctx, editing, search, onRemove, onRestore, onPreview, onExtract,
}: ArchiveViewerProps): React.JSX.Element {
  const [open, setOpen] = useState<ReadonlySet<string>>(() => new Set(['']));
  const [selected, setSelected] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [failure, setFailure] = useState<{ name: string; message: string } | null>(null);

  const tree = useMemo(() => buildTree(model), [model]);
  const matches = useMemo(() => matching(model, search), [model, search]);

  const toggle = (path: string): void => {
    setOpen((current) => {
      const next = new Set(current);
      if (next.has(path)) next.delete(path); else next.add(path);
      return next;
    });
  };

  /**
   * The one place anything is decompressed, and only from a click.
   * A failure here is a §34 message on the row, not a thrown error: the
   * archive is still perfectly listable with one bad entry in it.
   */
  const run = (entry: ArchiveEntry, then: (extracted: Uint8Array) => void): void => {
    setFailure(null);
    setBusy(entry.name);
    try {
      const result = extractEntry(bytes, model, entry.name, ctx);
      then(result.bytes);
    } catch (err) {
      const e = err as { messageKey?: string; message?: string };
      setFailure({ name: entry.name, message: e.message ?? '알 수 없는 오류' });
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="canvas archive">
      <div className="archive__summary">
        <span>
          파일 <b className="tnum">{model.fileCount}</b>개
          {model.directoryCount > 0 ? <> · 폴더 <b className="tnum">{model.directoryCount}</b>개</> : null}
        </span>
        <span aria-hidden="true">·</span>
        <span>전체 <b className="tnum">{formatFileSize('ko', model.totalUncompressed)}</b></span>
        {model.executableCount > 0 ? (
          <span className="archive__flag">
            <Icon name="alert" size={13} />
            실행 파일 {model.executableCount}개 — 열지 않습니다
          </span>
        ) : null}
        {model.encryptedCount > 0 ? (
          <span className="archive__flag">
            <Icon name="lock" size={13} />
            암호 보호 {model.encryptedCount}개
          </span>
        ) : null}
      </div>

      <div className="archive__list" role="tree" aria-label="압축 파일 내용">
        {tree.children.map((node) => (
          <Row
            key={node.path}
            node={node}
            depth={0}
            open={open}
            selected={selected}
            busy={busy}
            failure={failure}
            editing={editing}
            removed={model.removed}
            matches={matches}
            search={search}
            onToggle={toggle}
            onSelect={setSelected}
            onRemove={onRemove}
            onRestore={onRestore}
            onPreview={(entry) => run(entry, (extracted) => onPreview(entry.name, extracted))}
            onExtract={(entry) => run(entry, (extracted) => onExtract(entry.name, extracted))}
          />
        ))}
      </div>
    </div>
  );
}

/** Entry names matching the search box, plus the folders that lead to them. */
function matching(model: ArchiveModel, search: string): ReadonlySet<string> | null {
  if (!search.trim()) return null;
  const needle = search.toLowerCase();
  const out = new Set<string>();
  for (const entry of model.entries) {
    if (!entry.name.toLowerCase().includes(needle)) continue;
    entry.segments.forEach((_, i) => out.add(entry.segments.slice(0, i + 1).join('/')));
  }
  return out;
}

/* ------------------------------------------------------------------ *
 * rows
 * ------------------------------------------------------------------ */

interface RowProps {
  readonly node: ArchiveNode;
  readonly depth: number;
  readonly open: ReadonlySet<string>;
  readonly selected: string | null;
  readonly busy: string | null;
  readonly failure: { name: string; message: string } | null;
  readonly editing: boolean;
  readonly removed: ReadonlySet<string>;
  readonly matches: ReadonlySet<string> | null;
  readonly search: string;
  readonly onToggle: (path: string) => void;
  readonly onSelect: (path: string) => void;
  readonly onRemove: (name: string) => void;
  readonly onRestore: (name: string) => void;
  readonly onPreview: (entry: ArchiveEntry) => void;
  readonly onExtract: (entry: ArchiveEntry) => void;
}

function Row(props: RowProps): React.JSX.Element | null {
  const { node, depth, open, matches } = props;
  if (matches && !matches.has(node.path)) return null;

  const expanded = open.has(node.path) || (matches !== null);
  const entry = node.entry;
  const isSelected = props.selected === node.path;
  const isRemoved = entry !== null && props.removed.has(entry.name);
  const isBusy = props.busy === entry?.name;
  const failed = props.failure?.name === entry?.name ? props.failure : null;

  return (
    <>
      <div
        className={[
          'archive-row',
          isSelected ? 'archive-row--selected' : '',
          isRemoved ? 'archive-row--removed' : '',
        ].filter(Boolean).join(' ')}
        role="treeitem"
        aria-expanded={node.isDirectory ? expanded : undefined}
        aria-selected={isSelected}
        tabIndex={0}
        style={{ paddingInlineStart: `${8 + depth * 18}px` }}
        onClick={() => {
          props.onSelect(node.path);
          if (node.isDirectory) props.onToggle(node.path);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            props.onSelect(node.path);
            if (node.isDirectory) props.onToggle(node.path);
            else if (entry?.previewable) props.onPreview(entry);
          }
        }}
      >
        <span className="archive-row__twisty" aria-hidden="true">
          {node.isDirectory
            ? <Icon name={expanded ? 'chevron-down' : 'chevron-right'} size={13} />
            : null}
        </span>

        <Icon
          name={iconFor(node, entry)}
          size={16}
          className={`archive-row__icon${entry?.executable ? ' archive-row__icon--danger' : ''}`}
        />

        <span className="archive-row__name truncate">
          {props.search ? highlight(node.name, props.search) : node.name}
        </span>

        {entry?.executable ? (
          <span className="badge badge--danger" title="실행 파일입니다. 모두뷰어는 실행하지도, 열지도 않습니다 (§21)">
            실행 파일
          </span>
        ) : null}
        {entry?.encrypted ? (
          <span className="badge" title="암호로 보호된 항목입니다">암호</span>
        ) : null}
        {entry && !entry.isDirectory && entry.ratio > 100 ? (
          <span className="badge badge--warn" title={`압축 대비 ${Math.round(entry.ratio)}배로 풀립니다`}>
            {Math.round(entry.ratio)}×
          </span>
        ) : null}

        <span className="spacer" />

        <span className="archive-row__size tnum">
          {formatFileSize('ko', node.sizeBytes)}
        </span>

        <span className="archive-row__actions">
          {entry && !entry.isDirectory && !isRemoved ? (
            <>
              {entry.previewable ? (
                <button
                  className="btn btn--icon tip"
                  data-tip="미리보기"
                  aria-label={`${node.name} 미리보기`}
                  disabled={isBusy}
                  onClick={(e) => { e.stopPropagation(); props.onPreview(entry); }}
                >
                  <Icon name="eye" size={14} />
                </button>
              ) : null}
              {entry.extractable ? (
                <button
                  className="btn btn--icon tip"
                  data-tip="이 항목만 저장"
                  aria-label={`${node.name} 저장`}
                  disabled={isBusy}
                  onClick={(e) => { e.stopPropagation(); props.onExtract(entry); }}
                >
                  <Icon name="download" size={14} />
                </button>
              ) : null}
              {props.editing ? (
                <button
                  className="btn btn--icon tip"
                  data-tip="새 압축 파일에서 제외"
                  aria-label={`${node.name} 제외`}
                  onClick={(e) => { e.stopPropagation(); props.onRemove(entry.name); }}
                >
                  <Icon name="close" size={14} />
                </button>
              ) : null}
            </>
          ) : null}
          {isRemoved ? (
            <button
              className="btn btn--ghost"
              onClick={(e) => { e.stopPropagation(); props.onRestore(entry!.name); }}
            >
              되돌리기
            </button>
          ) : null}
        </span>
      </div>

      {failed ? (
        <div className="archive-row__error" style={{ paddingInlineStart: `${34 + depth * 18}px` }}>
          <Icon name="alert" size={13} />
          {failed.message}
        </div>
      ) : null}

      {node.isDirectory && expanded
        ? node.children.map((child) => (
          <Row key={child.path} {...props} node={child} depth={depth + 1} />
        ))
        : null}
    </>
  );
}

function iconFor(node: ArchiveNode, entry: ArchiveEntry | null): IconName {
  if (node.isDirectory) return 'grid';
  if (entry?.executable) return 'alert';
  if (entry?.encrypted) return 'lock';
  if (!entry?.formatId) return 'file';
  return iconForFormat(entry.formatId, FORMATS_BY_ID.get(entry.formatId)?.family);
}

function highlight(text: string, needle: string): React.ReactNode {
  const found = text.toLowerCase().indexOf(needle.toLowerCase());
  if (found < 0) return text;
  return (
    <>
      {text.slice(0, found)}
      <mark>{text.slice(found, found + needle.length)}</mark>
      {text.slice(found + needle.length)}
    </>
  );
}
